import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Offcanvas, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";

import { Pagination } from "swiper/modules";

import "./MarketPlace.css";
import ProfileImg from "../../../assets/images/Profile.svg";
import RoundStar from "../../../assets/images/Star.svg";
import Members from "../../../assets/images/user.svg";
import BlackStar from "../../../assets/images/onboardingImages/BlackStar.svg";
import BlackStarHalf from "../../../assets/images/onboardingImages/HalfStar.svg";
import FadeStar from "../../../assets/images/onboardingImages/fadeStar.svg";
import blockchainIcon from "../../../assets/images/CheckMark.svg";
import BubbleSvgButton from "../CommonComponents/BubbleSvgButton/BubbleSvgButton";
import Cross from "../../../assets/images/green-corss.svg";
import ReviewStar from "../../../assets/images/onboardingImages/ReviewStar.png";
import ReviewUser from "../../../assets/images/onboardingImages/ReviewUser.svg";
import verifiedCheck from "../../../assets/images/VerifiedCheck.svg";
import Exchange from "../../../assets/images/onboardingImages/Exchange.svg";
import BinanceLogo from "../../../assets/images/binance.svg";
import BybitLogo from "../../../assets/images/bybit.svg";
import MarketPlaceRanking from "./MarketPlaceRanking";
import CommunityAreaChart, { CommunityAreaChartTabs, TABS } from "./CommunityAreaChart";
import useApi from "../../../helpers/apiHelper/requestHelper";
import Table from "../../sharedPages/CommonComponents/Table";
import {
  fetchUser,
  formatPercentageToFourDecimals,
  isOlderThan7Days,
  simplifyTradingVolume,
} from "../../../helpers/helper";
import { useDispatch, useSelector } from "react-redux";
import { storeUser, updateUser } from "../../../redux/users/usersSlice";
import { useNavigate } from "react-router-dom";
import NoLinkMemberSetupScreen from "../../NewOnboarding/OnboardingCL/SetupScreen/NoLinkMemberSetupScreen";
import FilterBar, {
  FilterToggleButton,
  MarketParentFilter,
  DEFAULT_CHILD_FILTERS,
  DEFAULT_MARKET_FILTER,
} from "./Filterbar";
import SearchBar from "../CommonComponents/SearchBar/SearchBar";
import { toastify } from "../../../helpers/toast/toastify";
import ConfirmModal from "../CommonComponents/ConfirmModal/ConfirmModal";
import { setLoading } from "../../../redux/loading/loadingSlice";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatK(value) {
  const num = Number(value) || 0;
  if (Math.abs(num) >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toFixed(2);
}

const TRADE_MARKET_LABELS = {
  spot: "Spot",
  futures: "Futures",
  both: "Both",
  none: "None",
};

const BOTH_MARKET_TOOLTIP = "Futures & Spot";

const TradeMarketLabel = ({ value, asBadge = false }) => {
  const label = value || "Not Connected";
  if (
    !value ||
    value === "-" ||
    value === TRADE_MARKET_LABELS.none ||
    value === "Not Connected"
  ) {
    return "Not Connected";
  }

  if (!asBadge) {
    if (label !== TRADE_MARKET_LABELS.both) {
      return <>{label}</>;
    }

    return (
      <OverlayTrigger
        placement="top"
        trigger={["hover", "focus", "click"]}
        overlay={
          <Tooltip id="marketplace-market-both">{BOTH_MARKET_TOOLTIP}</Tooltip>
        }
      >
        <span
          className="tradeMarketBoth"
          tabIndex={0}
          role="button"
          onClick={(e) => e.stopPropagation()}
        >
          {label}
        </span>
      </OverlayTrigger>
    );
  }

  // Offcanvas only: "Both" → separate Spot + Futures badges
  if (label === TRADE_MARKET_LABELS.both) {
    return (
      <span className="tradeMarketBadgeGroup">
        <span className="tradeMarketBadge">{TRADE_MARKET_LABELS.spot}</span>
        <span className="tradeMarketBadge">{TRADE_MARKET_LABELS.futures}</span>
      </span>
    );
  }

  return <span className="tradeMarketBadge">{label}</span>;
};

const parsePermissionType = (value) => {
  if (value == null || value === "") return null;
  const normalized = String(value).toLowerCase().trim();
  if (normalized === "none") return "none";
  if (normalized === "future") return "futures";
  if (
    normalized === "spot" ||
    normalized === "futures" ||
    normalized === "both"
  ) {
    return normalized;
  }
  return null;
};

const formatTradeMarketLabel = (value) => {
  const parsed = parsePermissionType(value);
  return parsed ? TRADE_MARKET_LABELS[parsed] : null;
};

const formatExchangeLabel = (exchange) => {
  if (exchange == null || exchange === "") return null;
  const normalized = String(exchange).toLowerCase().trim();
  if (normalized === "binance") return "Binance";
  if (normalized === "bybit") return "Bybit";
  return String(exchange);
};

const deriveMarketFromCredentialFlags = (creds) => {
  if (!creds) return null;
  const spot = Boolean(creds.isConnectedSpot);
  const futures = Boolean(
    creds.isConnectedUsdFuture || creds.isConnectedDerivatives
  );
  if (spot && futures) return "both";
  if (spot) return "spot";
  if (futures) return "futures";
  return null;
};

const getActiveSubscription = (item) => {
  const subs = Array.isArray(item?.subscription) ? item.subscription : [];
  return (
    subs.find((s) => s?.isActive && !s?.isDeleted) ||
    subs.find((s) => s?.isActive) ||
    subs.find((s) => !s?.isDeleted) ||
    subs[0] ||
    null
  );
};

const getStatsObject = (source) => {
  if (!source) return {};
  if (Array.isArray(source.stats)) return source.stats[0] || {};
  if (source.stats && typeof source.stats === "object") return source.stats;
  return {};
};

const toPositiveNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    const nested = value.$numberDecimal ?? value.$numberLong ?? value.value;
    return toPositiveNumber(nested);
  }
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

/** Default market to display stats for when a CL isn't spot-only (futures-only or "both"). */
const resolveCardMarket = (item) =>
  parsePermissionType(item?.permissionType) === "spot" ? "spot" : "futures";

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Look up a stat that may be reported separately per market (e.g. "winRate"
 * vs "spotWinRate"). Tries the market-prefixed key(s) first when market is
 * spot, then falls back to the generic key(s) so cards keep working while
 * the backend doesn't yet send spot-scoped data for every field.
 */
const resolveScopedStat = (item, stats, market, keys) => {
  const keyList = Array.isArray(keys) ? keys : [keys];
  const candidates =
    market === "spot"
      ? [...keyList.map((k) => `spot${capitalize(k)}`), ...keyList]
      : keyList;

  for (const key of candidates) {
    const val = stats?.[key] ?? item?.[key];
    if (val != null && val !== "") return val;
  }
  return null;
};

/** Resolve avg trade duration in seconds from community/stats payload. */
const resolveAvgTradeDurationSeconds = (item, statsInput) => {
  const stats = statsInput || getStatsObject(item);
  const isSpot = parsePermissionType(item?.permissionType) === "spot";

  const secondsCandidates = isSpot
    ? [
        stats.spotAvgTradeDurationSeconds,
        item?.spotAvgTradeDurationSeconds,
        stats.avgTradeDurationSeconds,
        item?.avgTradeDurationSeconds,
      ]
    : [
        stats.avgTradeDurationSeconds,
        item?.avgTradeDurationSeconds,
        stats.spotAvgTradeDurationSeconds,
        item?.spotAvgTradeDurationSeconds,
      ];

  for (const candidate of secondsCandidates) {
    const seconds = toPositiveNumber(candidate);
    if (seconds != null) return seconds;
  }

  const msCandidates = isSpot
    ? [
        stats.spotAvgTradeDurationMs,
        item?.spotAvgTradeDurationMs,
        stats.avgTradeDurationMs,
        item?.avgTradeDurationMs,
      ]
    : [
        stats.avgTradeDurationMs,
        item?.avgTradeDurationMs,
        stats.spotAvgTradeDurationMs,
        item?.spotAvgTradeDurationMs,
      ];

  for (const candidate of msCandidates) {
    const ms = toPositiveNumber(candidate);
    if (ms != null) return ms / 1000;
  }

  // Last resort: any key on stats containing "duration"
  for (const [key, value] of Object.entries(stats || {})) {
    if (!/duration/i.test(key)) continue;
    const num = toPositiveNumber(value);
    if (num == null) continue;
    if (/ms$/i.test(key) || /milli/i.test(key)) return num / 1000;
    return num;
  }

  return null;
};

const formatAvgTradeDuration = (seconds) => {
  const total = toPositiveNumber(seconds);
  if (total == null) return "N/A";

  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);

  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.round(total)}s`;
};

const getCommunityTradeMarket = (item) => {
  const toTableLabel = (label) =>
    !label || label === TRADE_MARKET_LABELS.none ? "Not Connected" : label;

  if (Object.prototype.hasOwnProperty.call(item || {}, "permissionType")) {
    const fromCommunity = formatTradeMarketLabel(item.permissionType);
    if (fromCommunity) return toTableLabel(fromCommunity);
  }

  const exchange = String(item?.exchange ?? "").toLowerCase().trim();
  if (exchange === "binance") {
    const label = formatTradeMarketLabel(item?.binancePermissionType);
    if (label) return toTableLabel(label);
  }
  if (exchange === "bybit") {
    const label = formatTradeMarketLabel(item?.bybitPermissionType);
    if (label) return toTableLabel(label);
  }

  const fromBinance = formatTradeMarketLabel(item?.binancePermissionType);
  if (fromBinance) return toTableLabel(fromBinance);
  const fromBybit = formatTradeMarketLabel(item?.bybitPermissionType);
  if (fromBybit) return toTableLabel(fromBybit);

  // Legacy fallbacks only when the new API fields are absent
  const leader = item?.leaderUser || item?.leader || item?.owner || null;
  const binance =
    leader?.binanceCredentials || item?.binanceCredentials || null;
  const bybit = leader?.bybitCredentials || item?.bybitCredentials || null;

  const legacyCandidates = [
    item?.tradeMarketType,
    item?.tradeMarket,
    leader?.permissionType,
    binance?.isConnected ? binance?.permissionType : null,
    bybit?.isConnected ? bybit?.permissionType : null,
    deriveMarketFromCredentialFlags(binance),
    deriveMarketFromCredentialFlags(bybit),
  ];

  for (const candidate of legacyCandidates) {
    const label = formatTradeMarketLabel(candidate);
    if (label) return toTableLabel(label);
  }

  return "Not Connected";
};

const getCommunityExchangeLabel = (item) => {
  const fromApi = formatExchangeLabel(item?.exchange);
  if (fromApi) return fromApi;

  const leader = item?.leaderUser || item?.leader || null;
  const exchanges = [];
  if (leader?.binanceCredentials?.isConnected) exchanges.push("Binance");
  if (leader?.bybitCredentials?.isConnected) exchanges.push("Bybit");
  if (item?.binanceCredentials?.isConnected) exchanges.push("Binance");
  if (item?.bybitCredentials?.isConnected) exchanges.push("Bybit");
  return exchanges.length ? [...new Set(exchanges)].join(", ") : "Not Connected";
};

const EXCHANGE_LOGOS = {
  binance: BinanceLogo,
  bybit: BybitLogo,
};

const resolveExchangeLogos = (exchangeLabel) => {
  const label = String(exchangeLabel || "").trim();
  if (!label || label === "Not Connected" || label === "-") return [];

  return [...new Set(label.split(/[,&/|]+/).map((part) => part.trim().toLowerCase()))]
    .map((name) => {
      if (name.includes("binance")) {
        return { key: "binance", src: EXCHANGE_LOGOS.binance, alt: "Binance" };
      }
      if (name.includes("bybit")) {
        return { key: "bybit", src: EXCHANGE_LOGOS.bybit, alt: "Bybit" };
      }
      return null;
    })
    .filter(Boolean);
};

const ExchangeLogoRow = ({ exchangeLabel }) => {
  const logos = resolveExchangeLogos(exchangeLabel);
  if (!logos.length) {
    return <p>{exchangeLabel || "Not Connected"}</p>;
  }

  return (
    <div className="marketPlaceExchangeLogos">
      {logos.map((logo) => (
        <img
          key={logo.key}
          src={logo.src}
          alt={logo.alt}
          className="marketPlaceExchangeLogo"
        />
      ))}
    </div>
  );
};

const mapCommunitiesToTableData = (apiResponse) => {
  const communities = Array.isArray(apiResponse)
    ? apiResponse
    : apiResponse?.communities || [];

  return communities.map((item) => {
    const stats = item.stats?.[0] || {};
    const activeSub = getActiveSubscription(item);
    const totalReviews = item.totalReviews || 0;
    const ratingValue = `${item?.averageRating ?? 0}/5`;
    const ratingSmall =
      totalReviews > 0
        ? `(${totalReviews} Review${totalReviews > 1 ? "s" : ""})`
        : "(No Reviews)";

    // A spot-only CL's card/table row shows spot-scoped stats; futures-only
    // and "both" (default view) CLs show futures-scoped stats. See the
    // Statistics toggle in the offcanvas for how "both" CLs' spot numbers
    // are surfaced.
    const market = resolveCardMarket(item);

    // const pnlValue =
    //   Number(item.pnlPercentage ?? stats?.pnlPercentage ?? 0) || 0;
    // const rawWinRate = item.winRate ?? stats.winRate;
    // const winRateValue =
    //   rawWinRate == null || rawWinRate === "" ? null : Number(rawWinRate);
    // const thisWeekPnlValue =
    //   Number(stats.thisWeekPnlPercentage ?? item.thisWeekPnlPercentage ?? 0) || 0;
    // const avgLeverageRaw = item.averageLeverage ?? stats.leverageAvg;
    // const avgLeverage =
    //   avgLeverageRaw == null || avgLeverageRaw === ""
    //     ? null
    //     : Number(avgLeverageRaw);
    // const totalTrades = Number(item.totalTrades ?? stats.totalTrades ?? 0) || 0;
    // const avgTradeRoiRaw = stats.avgTradeRoi ?? stats.roi;
    // const avgTradeRoi =
    //   avgTradeRoiRaw == null || avgTradeRoiRaw === ""
    //     ? null
    //     : Number(avgTradeRoiRaw);
    // const aumValue = Number(item.AUM ?? 0) || 0;
    const pnlValue =
      Number(resolveScopedStat(item, stats, market, ["pnlPercentage"]) ?? 0) ||
      0;
    const rawWinRate = resolveScopedStat(item, stats, market, ["winRate"]);
    const winRateValue =
      rawWinRate == null || rawWinRate === "" ? null : Number(rawWinRate);
    const thisWeekPnlValue =
      Number(
        resolveScopedStat(item, stats, market, ["thisWeekPnlPercentage"]) ?? 0
      ) || 0;
    // Leverage doesn't apply to spot — only resolve it for futures.
    const avgLeverageRaw =
      market === "spot"
        ? null
        : resolveScopedStat(item, stats, "futures", [
            "averageLeverage",
            "leverageAvg",
          ]);
    const avgLeverage =
      avgLeverageRaw == null || avgLeverageRaw === ""
        ? null
        : Number(avgLeverageRaw);
    const totalTrades =
      Number(resolveScopedStat(item, stats, market, ["totalTrades"]) ?? 0) ||
      0;
    const avgTradeRoiRaw = resolveScopedStat(item, stats, market, [
      "avgTradeRoi",
      "roi",
    ]);
    const avgTradeRoi =
      avgTradeRoiRaw == null || avgTradeRoiRaw === ""
        ? null
        : Number(avgTradeRoiRaw);
    const aumValue =
      Number(resolveScopedStat(item, stats, market, ["AUM"]) ?? 0) || 0;

    return {
      ...item,
      name: item.disPlayName,
      defaultStatMarket: market,
      rating: ratingValue,
      ratingSmall,
      pnl: {
        value: `${formatK(pnlValue)}%`,
        className: pnlValue > 0 ? "greenText" : pnlValue < 0 ? "redText" : "",
      },
      winRate: {
        value:
          winRateValue == null || !Number.isFinite(winRateValue)
            ? "N/A"
            : `${formatK(winRateValue)}%`,
        className:
          winRateValue > 0 ? "greenText" : winRateValue < 0 ? "redText" : "",
      },
      aum: {
        value: `$${formatK(aumValue)}`,
        className: aumValue > 0 ? "greenText" : aumValue < 0 ? "redText" : "",
      },
      thisWeekPnlPercentage: {
        value: `${thisWeekPnlValue.toFixed(2)}%`,
        className:
          thisWeekPnlValue > 0
            ? "greenText"
            : thisWeekPnlValue < 0
              ? "redText"
              : "",
      },
      members: item?.participantsCount?.toString() || "0",
      price:
        activeSub?.monthlyfee != null && activeSub?.monthlyfee !== ""
          ? `$${activeSub.monthlyfee}`
          : "—",
      tradeMarket: getCommunityTradeMarket(item),
      exchangeLabel: getCommunityExchangeLabel(item),
      communityTrial: activeSub?.trialPeriodDays,
      communityMonthlyFee: activeSub?.monthlyfee,
      averageLeverage:
        avgLeverage != null && Number.isFinite(avgLeverage) ? avgLeverage : null,
      avgTradeRoi:
        avgTradeRoi != null && Number.isFinite(avgTradeRoi) ? avgTradeRoi : null,
      avgTradeDurationSeconds: resolveAvgTradeDurationSeconds(item, stats),
      totalTrades,
      totalReviews: item?.totalReviews,
      communityRanking: item?.myRanking || apiResponse?.totalCount,
      totalCommunities: apiResponse?.totalCount ?? item?.totalCommunities,
    };
  });
};

const getCommunityPermissionKey = (community) => {
  const fromPermission = parsePermissionType(community?.permissionType);
  // "none" means not connected — don't treat it as a real market
  if (fromPermission && fromPermission !== "none") return fromPermission;

  const label = String(community?.tradeMarket || "").trim();
  if (label === TRADE_MARKET_LABELS.spot || /^spot$/i.test(label)) return "spot";
  if (label === TRADE_MARKET_LABELS.futures || /^futures?$/i.test(label))
    return "futures";
  if (label === TRADE_MARKET_LABELS.both || /^both$/i.test(label)) return "both";
  if (
    !label ||
    label === "Not Connected" ||
    label === "-" ||
    label === TRADE_MARKET_LABELS.none
  ) {
    return null;
  }
  return null;
};

/** Parent market → which scoped stats child filters / display should use. */
const resolveFilterStatMarket = (community, marketFilter) => {
  if (marketFilter === "Spot") return "spot";
  if (marketFilter === "Futures") return "futures";
  return resolveCardMarket(community);
};

const communityMatchesParentMarket = (community, marketFilter) => {
  if (!marketFilter || marketFilter === "All") return true;

  const permission = getCommunityPermissionKey(community);

  // Both → only communities with permissionType both
  if (marketFilter === "Both") {
    return permission === "both";
  }

  // Spot → spot-only + both
  if (marketFilter === "Spot") {
    return permission === "spot" || permission === "both";
  }

  // Futures → futures-only + both
  if (marketFilter === "Futures") {
    return permission === "futures" || permission === "both";
  }

  // Not Connected → no usable market permission
  if (marketFilter === "Not Connected") {
    return permission == null;
  }

  return true;
};

/** Recompute display stats for the active parent market scope. */
const applyMarketScopeToCommunity = (community, marketFilter) => {
  if (!marketFilter || marketFilter === "All" || marketFilter === "Both") {
    return community;
  }

  const market = resolveFilterStatMarket(community, marketFilter);
  const stats = getStatsObject(community);

  const pnlValue =
    Number(resolveScopedStat(community, stats, market, ["pnlPercentage"]) ?? 0) ||
    0;
  const rawWinRate = resolveScopedStat(community, stats, market, ["winRate"]);
  const winRateValue =
    rawWinRate == null || rawWinRate === "" ? null : Number(rawWinRate);
  const thisWeekPnlValue =
    Number(
      resolveScopedStat(community, stats, market, ["thisWeekPnlPercentage"]) ?? 0
    ) || 0;
  const aumValue =
    Number(resolveScopedStat(community, stats, market, ["AUM"]) ?? 0) || 0;
  const totalTrades =
    Number(resolveScopedStat(community, stats, market, ["totalTrades"]) ?? 0) ||
    0;
  const avgTradeRoiRaw = resolveScopedStat(community, stats, market, [
    "avgTradeRoi",
    "roi",
  ]);
  const avgTradeRoi =
    avgTradeRoiRaw == null || avgTradeRoiRaw === ""
      ? null
      : Number(avgTradeRoiRaw);
  const avgLeverageRaw =
    market === "spot"
      ? null
      : resolveScopedStat(community, stats, "futures", [
          "averageLeverage",
          "leverageAvg",
        ]);
  const avgLeverage =
    avgLeverageRaw == null || avgLeverageRaw === ""
      ? null
      : Number(avgLeverageRaw);

  return {
    ...community,
    defaultStatMarket: market,
    pnl: {
      value: `${formatK(pnlValue)}%`,
      className: pnlValue > 0 ? "greenText" : pnlValue < 0 ? "redText" : "",
    },
    winRate: {
      value:
        winRateValue == null || !Number.isFinite(winRateValue)
          ? "N/A"
          : `${formatK(winRateValue)}%`,
      className:
        winRateValue > 0 ? "greenText" : winRateValue < 0 ? "redText" : "",
    },
    aum: {
      value: `$${formatK(aumValue)}`,
      className: aumValue > 0 ? "greenText" : aumValue < 0 ? "redText" : "",
    },
    thisWeekPnlPercentage: {
      value: `${thisWeekPnlValue.toFixed(2)}%`,
      className:
        thisWeekPnlValue > 0
          ? "greenText"
          : thisWeekPnlValue < 0
            ? "redText"
            : "",
    },
    averageLeverage:
      avgLeverage != null && Number.isFinite(avgLeverage) ? avgLeverage : null,
    avgTradeRoi:
      avgTradeRoi != null && Number.isFinite(avgTradeRoi) ? avgTradeRoi : null,
    totalTrades,
  };
};

const matchesFilters = (community, filters) => {
  const stats = getStatsObject(community);
  const marketFilter = filters.market || "All";

  // ── Parent Market (scopes the rest) ───────────────────────────────────────
  if (!communityMatchesParentMarket(community, marketFilter)) return false;

  const statMarket = resolveFilterStatMarket(community, marketFilter);

  // ── Ratings ──────────────────────────────────────────────────────────────
  if (filters.ratings !== "All") {
    const rating = community?.averageRating ?? 0;
    const starValue = Number(filters.ratings);
    const min = starValue === 1 ? 0 : starValue - 0.5;
    const max = starValue === 5 ? 5 : starValue + 0.5;
    if (rating < min || rating >= max) return false;
  }

  // ── Drawdown (market-scoped PNL) ─────────────────────────────────────────
  if (filters.drawdown !== "All") {
    const pnlNum =
      Number(
        resolveScopedStat(community, stats, statMarket, ["pnlPercentage"]) ?? 0
      ) || 0;
    const drawdown = pnlNum < 0 ? Math.abs(pnlNum) : 0;
    const [lo, hi] = parsePercentRange(filters.drawdown);
    if (drawdown < lo || drawdown > hi) return false;
  }

  // ── Activity (last trade) ─────────────────────────────────────────────────
  if (filters.activity !== "All") {
    const daysMap = {
      "Last 24 hours": 1,
      "Last 3 days": 3,
      "Last 7 days": 7,
      "Last 14 days": 14,
      "Last Month": 30,
    };
    const maxDays = daysMap[filters.activity];
    if (maxDays !== undefined) {
      const lastTrade =
        resolveScopedStat(community, stats, statMarket, [
          "lastTradeTime",
          "lastTrade",
        ]) ?? stats.lastTradeTime;
      if (!lastTrade) return false;
      const daysSince =
        (Date.now() - Number(lastTrade) * 1000) / (1000 * 60 * 60 * 24);
      if (daysSince > maxDays) return false;
    }
  }

  // ── Min. Funding ──────────────────────────────────────────────────────────
  if (filters.minFunding !== "All") {
    const activeSub = getActiveSubscription(community);
    const funding =
      stats.minimumFunding ?? activeSub?.minimumFunding ?? 0;
    const [lo, hi] = parseDollarRange(filters.minFunding);
    if (Number(funding) < lo || Number(funding) > hi) return false;
  }

  // ── Price ─────────────────────────────────────────────────────────────────
  if (filters.price !== "All") {
    const activeSub = getActiveSubscription(community);
    const monthlyFee = activeSub?.monthlyfee ?? 0;
    const [lo, hi] = parseDollarRange(filters.price);
    if (Number(monthlyFee) < lo || Number(monthlyFee) > hi) return false;
  }

  // ── Verification ──────────────────────────────────────────────────────────
  if (filters.verification !== "All") {
    const isVerified = stats.isVerified ?? false;
    if (filters.verification === "Yes" && !isVerified) return false;
    if (filters.verification === "No" && isVerified) return false;
  }

  // ── Trial ─────────────────────────────────────────────────────────────────
  if (filters.trial !== "All") {
    const hasTrial = stats.trial ?? false;
    if (filters.trial === "Yes" && !hasTrial) return false;
    if (filters.trial === "No" && hasTrial) return false;
  }

  // ── PNL (market-scoped) ───────────────────────────────────────────────────
  if (filters.pnl !== "All") {
    const pnlNum =
      Number(
        resolveScopedStat(community, stats, statMarket, ["pnlPercentage"]) ?? 0
      ) || 0;
    const [lo, hi] = parsePnlRange(filters.pnl);
    if (pnlNum < lo || pnlNum > hi) return false;
  }

  return true;
};

// ── Range parsers ─────────────────────────────────────────────────────────

/** Parse "10-25%" → [10, 25]. Handles "Above 100%" → [100, Infinity]. */
const parsePercentRange = (label) => {
  if (!label || label === "All") return [0, Infinity];
  const above = label.match(/above\s*([\d.]+)/i);
  if (above) return [Number(above[1]), Infinity];
  const below = label.match(/below\s*([\d.]+)/i);
  if (below) return [0, Number(below[1])];
  const range = label.match(/([\d.]+)\s*(?:to|-)\s*([\d.]+)/i);
  if (range) return [Number(range[1]), Number(range[2])];
  return [0, Infinity];
};

/** Parse "$100-250" or "Below $100" or "Above 1000+" → [lo, hi] */
const parseDollarRange = (label) => {
  if (!label || label === "All") return [0, Infinity];
  const above = label.match(/above\s*\$?([\d,]+)/i);
  if (above) return [Number(above[1].replace(/,/g, "")), Infinity];
  const below = label.match(/below\s*\$?([\d,]+)/i);
  if (below) return [0, Number(below[1].replace(/,/g, ""))];
  const range = label.match(/\$?([\d,]+)\s*[-–]\s*\$?([\d,]+)/i);
  if (range)
    return [
      Number(range[1].replace(/,/g, "")),
      Number(range[2].replace(/,/g, "")),
    ];
  return [0, Infinity];
};

/** Parse "0 to 10%" → [0, 10]. Handles "Above 100%" → [100, Infinity]. */
const parsePnlRange = (label) => parsePercentRange(label);

// ---------------------------------------------------------------------------
// Sub-components (unchanged from original)
// ---------------------------------------------------------------------------

const StarRating = ({ averageRating }) => {
  const rating = averageRating || 0;
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="starBox">
      {[...Array(fullStars)].map((_, index) => (
        <img key={`full-${index}`} src={BlackStar} alt="star" />
      ))}
      {hasHalfStar && (
        <img key="half" src={BlackStarHalf} alt="half star" />
      )}
      {[...Array(emptyStars)].map((_, index) => (
        <img key={`empty-${index}`} src={FadeStar} alt="empty star" />
      ))}
    </div>
  );
};

const MarketPlaceCommunityCard = ({ handleShow, cardData }) => {
  const marketKey = parsePermissionType(cardData?.permissionType);
  const marketLabel =
    marketKey && marketKey !== "none" ? TRADE_MARKET_LABELS[marketKey] : null;

  return (
    <div className="MarketPlaceCommunityCard" onClick={handleShow}>
      {marketLabel ? (
        <div className="MarketPlaceCommunityCard__marketBadge">
          {marketLabel}
        </div>
      ) : null}
      <div className="infoBox">
        <div className="imgBox">
          <img src={cardData?.logo || ProfileImg} alt="Profile" />
        </div>
        <div className="contentBox">
          <p className="name">
            {cardData?.name}
            {cardData?.stats?.[0]?.isVerified && (
              <img
                src={verifiedCheck}
                alt="verified"
                className="communityVerifiedTick"
              />
            )}
          </p>
          <div className="reviewBox">
            <div className="starBox">
              <StarRating averageRating={cardData?.averageRating} />
            </div>
            <div className="reviewCount">
              <p>
                {cardData?.averageRating} ({`${cardData?.totalReviews} Reviews`}
                )
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="statBox">
        <div>
          <p className="text">PnL (3M)</p>
          <p className="numbers">{cardData?.pnl?.value ?? "-"}</p>
        </div>
        <div>
          <p className="text">Win Rate</p>
          <p className="numbers">{cardData?.winRate?.value ?? "-"}</p>
        </div>
        <div>
          <p className="text">AUM</p>
          <p className="numbers">{cardData?.aum?.value ?? "-"}</p>
        </div>
      </div>
      <div className="footerBox">
        <div>
          <p className="footerText">
            ${cardData?.communityMonthlyFee}
            <span>/month</span>
          </p>
        </div>
        <div>
          <p className="footerText">{cardData?.communityTrial}d trial</p>
        </div>
        <div>
          <p className="footerText">{cardData?.members} members</p>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const MarketPlace = () => {
  const community = useSelector((state) => state?.user?.user?.leaderCommunity);
  const user = useSelector((state) => state.user);
  const loggedInUser = useSelector((state) => state?.user?.user);
  const subscribedCommunity =
    loggedInUser?.memberCommunities?.[0]?.community;
  const isSubscribed = Boolean(
    loggedInUser?.memberCommunities?.[0]?.subscribed
  );
  const subscribedCommunityId =
    subscribedCommunity?._id != null
      ? String(subscribedCommunity._id)
      : null;
  const subscribedCommunityName =
    subscribedCommunity?.disPlayName ||
    subscribedCommunity?.name ||
    "your current community";
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const [show, setShow] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const isSubscribedToThisCommunity = Boolean(
    isSubscribed &&
      subscribedCommunityId &&
      selectedCard?._id != null &&
      subscribedCommunityId === String(selectedCard._id)
  );
  const isAlreadySubscribedElsewhere = Boolean(
    isSubscribed && !isSubscribedToThisCommunity
  );
  const targetCommunityName =
    selectedCard?.disPlayName || selectedCard?.name || "this community";

  const isExistingMember = Boolean(
    loggedInUser?.role === "member" ||
      loggedInUser?.binanceCredentials?.isConnected ||
      loggedInUser?.bybitCredentials?.isConnected
  );
  // const [activeButton, setActiveButton] = useState("Day");
  const [activeTab, setActiveTab] = useState(0);
  const activeButton = TABS[activeTab]?.duration ?? "Day";
  const handleClose = () => setShow(false);
  const api = useApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showSetupModal, setShowSetupModal] = useState(false);
  // Which market the offcanvas Statistics section shows. Fixed to the CL's
  // single market for spot-only/futures-only CLs; user-togglable for CLs
  // with permissionType "both" (see the toggle rendered next to "Statistics").
  const [modalMarket, setModalMarket] = useState("futures");
  const selectedCardPermission = parsePermissionType(
    selectedCard?.permissionType
  );
  const canToggleModalMarket = selectedCardPermission === "both";

  const handleStart = async (card) => {
    if (isAlreadySubscribedElsewhere) {
      setShowSwitchConfirm(true);
      return;
    }

    if (isExistingMember) {
      try {
        const response = await api(
          "post",
          "NoLinkMember/convertNoLinkMemberToMember",
          { communityId: card?._id }
        );
        if (response?.status) {
          await fetchUser(api, dispatch, storeUser);
          navigate("/member/changeCommunity/subscription");
        } else {
          toastify(response?.message || "Something went wrong");
        }
      } catch (err) {
        toastify("Something went wrong. Please try again.");
      }
      return;
    }

    setShow(false);
    setShowSetupModal(true);
  };

  const handleSwitchCommunity = async (card) => {
    const currentMembership = loggedInUser?.memberCommunities?.[0];
    const oldCommunityId = currentMembership?.community?._id;
    if (!oldCommunityId || !card?._id || !loggedInUser?._id) {
      toastify("Unable to switch community. Please try again.");
      setShowSwitchConfirm(false);
      return;
    }

    // Keep the modal open and show loader — don't close it yet.
    setSwitchLoading(true);
    dispatch(setLoading(true));
    try {
      const unsubResponse = await api("post", "users/unsubscribe", {
        sessionId: currentMembership?.sessionId,
        communityId: oldCommunityId,
        userId: loggedInUser._id,
      });

      if (!unsubResponse?.status) {
        toastify(
          unsubResponse?.message ||
            "Could not cancel your current subscription. Please try again."
        );
        return;
      }

      // Always fetch fresh user state after unsubscribe so the backend
      // has fully processed the cancellation before we try to join B.
      await fetchUser(api, dispatch, storeUser);

      const convertResponse = await api(
        "post",
        "NoLinkMember/convertNoLinkMemberToMember",
        { communityId: card._id }
      );

      if (convertResponse?.status) {
        // Successfully joined community B — go straight to payment.
        await fetchUser(api, dispatch, storeUser);
        setShowSwitchConfirm(false);
        setShow(false);
        navigate("/member/changeCommunity/subscription");
      } else {
        // convertNoLinkMemberToMember can fail for existing members.
        // Fall back to the setup modal so the user can still join B and pay.
        setShowSwitchConfirm(false);
        setShow(false);
        setShowSetupModal(true);
      }
    } catch (err) {
      toastify("Something went wrong. Please try again.");
    } finally {
      setSwitchLoading(false);
      dispatch(setLoading(false));
    }
  };

  // Raw data from API
  const [data, setData] = useState([]);

  // Active filter state — child filters from FilterBar; market is parent scope
  const [marketFilter, setMarketFilter] = useState(DEFAULT_MARKET_FILTER);
  const [activeFilters, setActiveFilters] = useState(DEFAULT_CHILD_FILTERS);

  // Search query
  const [searchQuery, setSearchQuery] = useState("");
  // Sort state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });
  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" };
    });
  }, []);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // ── Derived: filtered data ────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let result = Array.isArray(data) ? data : [];

    // Search by community display name
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((c) =>
        (c?.name || c?.disPlayName || "").toLowerCase().includes(q),
      );
    }

    const filtersWithMarket = { ...activeFilters, market: marketFilter };

    // Parent market scopes the list first, then child filters apply within it
    const hasActiveFilter =
      marketFilter !== DEFAULT_MARKET_FILTER ||
      Object.values(activeFilters).some((v) => v !== "All");
    if (hasActiveFilter) {
      result = result.filter((c) => matchesFilters(c, filtersWithMarket));
    }

    // Show spot/futures-scoped stats when parent market is set
    if (marketFilter === "Spot" || marketFilter === "Futures") {
      result = result.map((c) => applyMarketScopeToCommunity(c, marketFilter));
    }

    // Apply sorting
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        if (
          sortConfig.key === "pnl" ||
          sortConfig.key === "winRate" ||
          sortConfig.key === "aum"
        ) {
          valA =
            parseFloat(String(valA?.value || "0").replace(/[^0-9.-]+/g, "")) ||
            0;
          valB =
            parseFloat(String(valB?.value || "0").replace(/[^0-9.-]+/g, "")) ||
            0;
        } else if (sortConfig.key === "rating") {
          valA = parseFloat(a.averageRating || 0);
          valB = parseFloat(b.averageRating || 0);
        } else if (sortConfig.key === "members") {
          valA = parseInt(a.members || 0, 10);
          valB = parseInt(b.members || 0, 10);
        } else if (sortConfig.key === "price") {
          valA =
            parseFloat(String(a.price || "0").replace(/[^0-9.-]+/g, "")) || 0;
          valB =
            parseFloat(String(b.price || "0").replace(/[^0-9.-]+/g, "")) || 0;
        } else if (typeof valA === "string" && typeof valB === "string") {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [data, activeFilters, marketFilter, searchQuery, sortConfig]);

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters, marketFilter, searchQuery]);

  const paginatedData = filteredData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // ── Stats helper ──────────────────────────────────────────────────────────
  // const getStatsDataFromCard = (card) => {
  //   if (!card) return [];
  //   const stats = getStatsObject(card);
  //
  //   const winRate =
  //     card?.winRate?.value ??
  //     (stats.winRate != null || card.winRate != null
  //       ? `${formatK(Number(card.winRate ?? stats.winRate ?? 0))}%`
  //       : "N/A");
  //
  //   const avgTradeRoiRaw = card?.avgTradeRoi ?? stats.avgTradeRoi ?? stats.roi;
  //   const avgTradeRoi =
  //     avgTradeRoiRaw == null || avgTradeRoiRaw === ""
  //       ? "N/A"
  //       : `${simplifyTradingVolume(avgTradeRoiRaw)}%`;
  //
  //   const aum = card?.aum?.value ?? "N/A";
  //
  //   const avgLeverageRaw =
  //     card?.averageLeverage ?? stats.leverageAvg ?? null;
  //   const avgLeverage =
  //     avgLeverageRaw == null ||
  //     avgLeverageRaw === "" ||
  //     !Number.isFinite(Number(avgLeverageRaw)) ||
  //     Number(avgLeverageRaw) === 0
  //       ? "N/A"
  //       : `${Number(avgLeverageRaw)}x`;
  //
  //   const totalTrades = card?.totalTrades ?? stats.totalTrades ?? 0;
  //   const avgTradeDuration = formatAvgTradeDuration(
  //     card?.avgTradeDurationSeconds ??
  //       resolveAvgTradeDurationSeconds(card, stats)
  //   );
  //
  //   return [
  //     { title: "Win Rate", numbers: winRate },
  //     { title: "Avg. Trade ROI", numbers: avgTradeRoi },
  //     { title: "AUM", numbers: aum },
  //     { title: "Avg. Trade Duration", numbers: avgTradeDuration },
  //     { title: "Max. Drawdown", numbers: "N/A" },
  //     { title: "Avg Leverage", numbers: avgLeverage },
  //     { title: "Total Trades", numbers: String(totalTrades) },
  //   ];
  // };

  // Market-aware version: `market` is "spot" or "futures". Spot-only CLs
  // and futures-only CLs always pass their fixed market; CLs with "both"
  // pass whichever market the Statistics toggle is set to (see modalMarket).
  const getStatsDataFromCard = (card, market = "futures") => {
    if (!card) return [];
    const stats = getStatsObject(card);
    const isSpot = market === "spot";

    const winRateRaw = resolveScopedStat(card, stats, market, ["winRate"]);
    const winRate =
      winRateRaw == null || winRateRaw === ""
        ? "N/A"
        : `${formatK(Number(winRateRaw))}%`;

    const avgTradeRoiRaw = resolveScopedStat(card, stats, market, [
      "avgTradeRoi",
      "roi",
    ]);
    const avgTradeRoi =
      avgTradeRoiRaw == null || avgTradeRoiRaw === ""
        ? "N/A"
        : `${simplifyTradingVolume(avgTradeRoiRaw)}%`;

    const aumRaw = resolveScopedStat(card, stats, market, ["AUM"]);
    const aum =
      aumRaw == null || aumRaw === ""
        ? "N/A"
        : `$${formatK(Number(aumRaw) || 0)}`;

    const totalTrades =
      resolveScopedStat(card, stats, market, ["totalTrades"]) ?? 0;
    const avgTradeDuration = formatAvgTradeDuration(
      resolveAvgTradeDurationSeconds({ ...card, permissionType: market }, stats)
    );

    const statsData = [
      { title: "Win Rate", numbers: winRate },
      { title: "Avg. Trade ROI", numbers: avgTradeRoi },
      { title: "AUM", numbers: aum },
      { title: "Avg. Trade Duration", numbers: avgTradeDuration },
      // { title: "Max. Drawdown", numbers: "N/A" },
    ];

    // Leverage doesn't apply to spot trading — omit the tile instead of
    // showing a meaningless "N/A" for spot-only / spot-toggled views.
    if (!isSpot) {
      const avgLeverageRaw = resolveScopedStat(card, stats, "futures", [
        "averageLeverage",
        "leverageAvg",
      ]);
      const avgLeverage =
        avgLeverageRaw == null ||
        avgLeverageRaw === "" ||
        !Number.isFinite(Number(avgLeverageRaw)) ||
        Number(avgLeverageRaw) === 0
          ? "N/A"
          : `${Number(avgLeverageRaw)}x`;
      statsData.push({ title: "Avg Leverage", numbers: avgLeverage });
    }

    statsData.push({ title: "Total Trades", numbers: String(totalTrades) });

    return statsData;
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      label: "Group Name",
      key: "name",
      width: "22%",
      type: "profile",
      onClick: (data) => handleShow(data),
      sortable: false,
      render: (row) => (
        <>
          {row?.name || row?.disPlayName || "Trading Group Name"}
          {row?.stats?.[0]?.isVerified && (
            <img
              src={verifiedCheck}
              alt="verified"
              className="communityVerifiedTick"
            />
          )}
        </>
      ),
    },
    {
      label: "Rating",
      key: "rating",
      keySmall: "ratingSmall",
      width: "13%",
      sortable: true,
    },
    {
      label: "Market",
      key: "tradeMarket",
      width: "12%",
      sortable: true,
      render: (row) => <TradeMarketLabel value={row.tradeMarket} />,
    },
    { label: "Price", key: "price", width: "10%", sortable: true },
    { label: "PNL (YTD)", key: "pnl", width: "11%", sortable: true },
    { label: "Win Rate", key: "winRate", width: "11%", sortable: true },
    { label: "AUM", key: "aum", width: "11%", sortable: true },
    { label: "Members", key: "members", width: "10%", sortable: false },
  ];

  // ── API ───────────────────────────────────────────────────────────────────
  const getAllCommunities = async () => {
    const response = await api("post", "NoLinkMember/allCommunities");
    const mapped = mapCommunitiesToTableData(response?.data || {});
    setData(Array.isArray(mapped) ? mapped : []);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    getAllCommunities();
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleShow = async (card) => {
    setSelectedCard(card);
    setShow(true);
    // Reset the Statistics market toggle for the newly opened card: spot-only
    // CLs open on spot, everyone else (futures-only, and "both" by default)
    // opens on futures.
    setModalMarket(
      parsePermissionType(card?.permissionType) === "spot" ? "spot" : "futures"
    );
    if (!card?._id && !card?.disPlayName && !card?.name) return;

    try {
      const response = await api("post", "NoLinkMember/getCommunityStats", {
        id: card._id,
        communityName: card.disPlayName || card.name,
      });
      if (!response?.status) return;

      const detail = Array.isArray(response?.data)
        ? response.data[0]
        : response?.data;
      if (!detail) return;

      const mergedStats = Array.isArray(detail.stats)
        ? detail.stats
        : detail.stats
          ? [detail.stats]
          : card.stats;
      const durationSeconds = resolveAvgTradeDurationSeconds(
        { ...card, ...detail },
        getStatsObject({ stats: mergedStats })
      );

      setSelectedCard((prev) => {
        if (!prev) return prev;
        if (card._id && String(prev._id) !== String(card._id)) return prev;
        return {
          ...prev,
          stats: mergedStats || prev.stats,
          avgTradeDurationSeconds:
            durationSeconds ?? prev.avgTradeDurationSeconds ?? null,
        };
      });
    } catch (_) {
      // Keep list data if detail fetch fails
    }
  };

  const handleFilterChange = useCallback((filters) => {
    setActiveFilters(filters);
  }, []);

  const handleMarketFilterChange = useCallback((market) => {
    setMarketFilter(market);
  }, []);

  const handleResetMarket = useCallback(() => {
    setMarketFilter(DEFAULT_MARKET_FILTER);
  }, []);

  const handleSearchChange = useCallback((e) => {
    const value = typeof e === "string" ? e : (e?.target?.value ?? "");
    setSearchQuery(value);
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="marketplacePage">
      {/* ── Top swiper (top-5 by PNL) ── */}
      <Swiper
        spaceBetween={30}
        pagination={{ clickable: true }}
        modules={[Pagination]}
        className="marketplaceSwiper"
        breakpoints={{
          0: { slidesPerView: 1 },
          780: { slidesPerView: 2 },
          945: { slidesPerView: 2 },
          1038: { slidesPerView: 3 },
          1364: { slidesPerView: 3 },
          1440: { slidesPerView: 3 },
          1700: { slidesPerView: 3 },
        }}
      >
        {(Array.isArray(data) ? data : []).slice(0, 5).map((cardData, i) => (
          <SwiperSlide key={i}>
            <MarketPlaceCommunityCard
              handleShow={() => handleShow(cardData)}
              cardData={cardData}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* ── Table card ── */}
      <div className="MArketPlaceTableCard">
        <div className="marketPlace_filters gap-2">
          <div className="marketPlace_filtersLeft">
            <FilterToggleButton
              isExpanded={filtersExpanded}
              onToggle={() => setFiltersExpanded((prev) => !prev)}
            />
            <MarketParentFilter
              value={marketFilter}
              onChange={handleMarketFilterChange}
            />
          </div>
          <SearchBar
            value={searchQuery}
            onChange={handleSearchChange}
            searchValue="Search communities..."
          />
        </div>

        <FilterBar
          isExpanded={filtersExpanded}
          onChange={handleFilterChange}
          onResetMarket={handleResetMarket}
          marketFilter={marketFilter}
        />

        <div className="row">
          <div className="">
            {paginatedData?.map((row, idx) => (
              <div className="d-md-none d-block" key={row.id || idx}>
                <div
                  className="marketPlace__card mb-3"
                  onClick={() => handleShow(row)}
                >
                  <div className="marketPlace__user">
                    <img src={row?.logo || ProfileImg} alt="Avatar" />
                  </div>
                  <div className="marketPlace__content fw-medium">
                    <div className="marketPlace_title">
                      {row?.name || row.disPlayName || "Trading Group Name"}
                      {row?.stats?.[0]?.isVerified && (
                        <div>
                          <img src={verifiedCheck} alt="verified" width={15} />
                        </div>
                      )}
                    </div>
                    <div className="marketPlace_details">
                      <div className="marketPlace_info">
                        <img src={Members} alt="Members" />
                        <p>{row.members ?? 0}</p>
                      </div>
                      <div className="marketPlace_info">
                        <img src={RoundStar} alt="Ratings" />
                        <p>{row.averageRating ?? "N/A"}</p>
                      </div>
                      <div className="marketPlace_info">
                        <p>
                          <span className="marketPlace_metaLabel">Market</span>
                          <TradeMarketLabel value={row.tradeMarket} />
                        </p>
                      </div>
                      <div className="marketPlace_info">
                        <p>
                          <span className="marketPlace_metaLabel">PNL</span>
                          {row.pnl && row.pnl.value
                            ? (() => {
                                const pnlVal =
                                  typeof row.pnl.value === "string"
                                    ? row.pnl.value.replace("%", "")
                                    : row.pnl.value;
                                return Number(pnlVal) > 0
                                  ? `+${pnlVal}%`
                                  : `${pnlVal}%`;
                              })()
                            : "N/A"}
                        </p>
                      </div>
                      <div className="marketPlace_info">
                        <p>
                          {row.aum && row.aum.value
                            ? (() => {
                                const valueNum = parseFloat(
                                  String(row.aum.value).replace(/[^\d.-]/g, ""),
                                );
                                if (isNaN(valueNum)) return `$${row.aum.value}`;
                                if (valueNum >= 1_000_000_000)
                                  return `$${(valueNum / 1_000_000_000).toFixed(2)}B`;
                                if (valueNum >= 1_000_000)
                                  return `$${(valueNum / 1_000_000).toFixed(2)}M`;
                                if (valueNum >= 1_000)
                                  return `$${(valueNum / 1_000).toFixed(2)}K`;
                                return `$${valueNum}`;
                              })()
                            : "AUM N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Table
            columns={columns}
            data={paginatedData}
            currentPage={currentPage}
            totalPages={Math.ceil(filteredData?.length / itemsPerPage)}
            onPageChange={setCurrentPage}
            onSort={handleSort}
            sortConfig={sortConfig}
          />
        </div>
      </div>

      {/* ── Detail offcanvas ── */}
      <Offcanvas
        className="CommunityOffcanvas marketPlaceCanvas"
        show={show}
        onHide={handleClose}
        placement="end"
        keyboard={false}
      >
        <Offcanvas.Body>
          <button className="closeBtn" onClick={handleClose}>
            <img src={Cross} alt="" />
          </button>

          <div className="infoTopBox">
            <div className="header-modal">
              <div className="">
                <div className="infoLeft">
                  <div className="imgBox">
                    <img
                      src={selectedCard?.logo || ProfileImg}
                      alt=""
                      className="logo-img"
                      // if you want to add a click handler for "view profile image", do it here
                    />
                  </div>
                  <div className="contantBox">
                    <p className="createdDate">{`Group created on ${formatDate(selectedCard?.createdAt)}`}</p>
                    <p className="name">
                      {selectedCard?.disPlayName}
                      {selectedCard?.stats?.[0]?.isVerified && (
                        <img
                          src={verifiedCheck}
                          alt="verified"
                          className="communityVerifiedTick"
                        />
                      )}
                    </p>
                    <div className="trialBox">
                      <p className="fs-12 whitespace-nowrap">
                        {selectedCard?.communityTrial > 0 && (
                          <><p className="fw-medium d-inline-flex">{selectedCard?.communityTrial}d</p> Trial &#8226; </>
                        )}
                        From{" "}
                        <span className="fs-12">
                          <p className="fw-medium d-inline-flex">${selectedCard?.communityMonthlyFee}</p> /mo
                        </span>
                      </p>
                    </div>
                    <p className="exchangeText">
                      <span className="exchangeTextLabel">Market:</span>
                      <TradeMarketLabel
                        value={selectedCard?.tradeMarket}
                        asBadge
                      />
                    </p>
                  </div>
                </div>
              </div>
              <div className="">
                <div className="infoRight fs-12">
                  <div className="subAndReviewBox">
                    <div className="RevBox">
                      <img src={ReviewStar} alt="" />
                      <div className="d-flex gap-1">
                        <p className="fw-medium">
                          {selectedCard?.averageRating}{" "}
                        </p>
                        <p>({`${selectedCard?.totalReviews} Reviews`})</p>
                      </div>
                    </div>
                    <div className="RevBox">
                      <img src={ReviewUser} alt="" />
                      <div className="d-flex gap-1">
                        <p className="fw-medium">{selectedCard?.members}</p>
                        <p className="fs-12">Subscribers</p>
                      </div>
                    </div>
                  </div>
                  <div className="subAndReviewBox fs-12">
                    <div className="RevBox">
                      <img src={blockchainIcon} alt="BlockChain" />
                      <p>
                        {selectedCard?.stats?.[0]?.isVerified
                          ? "Blockchain Verified"
                          : "Blockchain not verified"}
                      </p>
                    </div>
                    <div className="RevBox">
                      <img src={Exchange} alt="Exchange" />
                      <ExchangeLogoRow
                        exchangeLabel={
                          selectedCard?.exchangeLabel ||
                          getCommunityExchangeLabel(selectedCard) ||
                          "Not Connected"
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="">
                {Number(selectedCard?.communityTrial) ? (
                  <div className="btnBox">
                    <BubbleSvgButton
                      text={
                        isSubscribedToThisCommunity
                          ? "Already subscribed"
                          : `Start Free ${selectedCard?.communityTrial}-day trial`
                      }
                      type="button"
                      variant={
                        isSubscribedToThisCommunity ? "lightGray" : "black"
                      }
                      disabled={isSubscribedToThisCommunity}
                      containerStyling={{ justifyContent: "center" }}
                      onClick={() => {
                        if (isSubscribedToThisCommunity) return;
                        handleStart(selectedCard);
                      }}
                    />
                    <p className="fs-10 text-center mt-1">100% Automated. Cancel Anytime.</p>
                  </div>
                ) : (
                  <BubbleSvgButton
                    text={
                      isSubscribedToThisCommunity
                        ? "Already subscribed"
                        : "Start"
                    }
                    type="button"
                    variant={
                      isSubscribedToThisCommunity ? "lightGray" : "green"
                    }
                    disabled={isSubscribedToThisCommunity}
                    containerStyling={{ justifyContent: "center" }}
                    onClick={() => {
                      if (isSubscribedToThisCommunity) return;
                      handleStart(selectedCard);
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <hr className="firstLine" />

          <div className="GraphSecondRow">
            <div className="GraphMainBox">
              <div className="topTradingBox">
                <div className="contentSide">
                  <p className="name">Overall Performance</p>
                  <p className="trading__date fs-12">
                    {(() => {
                      let startDate, formattedRange;
                      const today = new Date();
                      const leaderCreatedAt =
                        selectedCard?.leaderUser?.createdAt;
                      const userCreatedAt = leaderCreatedAt
                        ? new Date(leaderCreatedAt)
                        : null;
                      switch (activeButton) {
                        case "Day":
                          formattedRange = formatDate(today);
                          break;
                        case "Week": {
                          const weekStart = new Date(today);
                          weekStart.setDate(today.getDate() - today.getDay());
                          startDate =
                            userCreatedAt && weekStart < userCreatedAt
                              ? userCreatedAt
                              : weekStart;
                          formattedRange = `${formatDate(startDate)} - ${formatDate(today)}`;
                          break;
                        }
                        case "Month": {
                          const monthStart = new Date(
                            today.getFullYear(),
                            today.getMonth(),
                            1,
                          );
                          startDate =
                            userCreatedAt && monthStart < userCreatedAt
                              ? userCreatedAt
                              : monthStart;
                          formattedRange = `${formatDate(startDate)} - ${formatDate(today)}`;
                          break;
                        }
                        case "All Time":
                          formattedRange = userCreatedAt
                            ? `${formatDate(userCreatedAt)} - ${formatDate(today)}`
                            : "N/A";
                          break;
                        default:
                          formattedRange = "N/A";
                      }
                      return formattedRange;
                    })()}
                  </p>
                </div>
                <div className="switchSide marketPlace">
                  <CommunityAreaChartTabs activeTab={activeTab} onTabChange={setActiveTab} />
                </div>
              </div>

              <div className="chartBox market_chart">
                {/* <CommunityBarChart key={selectedCard?.leaderUser?._id ? `${selectedCard.leaderUser._id}-${activeButton}` : activeButton} graphType={"pnl"} userId={selectedCard?.leaderUser?._id} duration={activeButton.toLocaleLowerCase()} /> */}
                <CommunityAreaChart
          bare
          graphType="pnl"
                  userId={selectedCard?.leaderUser?._id}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  market={canToggleModalMarket ? modalMarket : undefined}
                />
              </div>
            </div>
          </div>

          <hr className="firstLine two" />

          <div className="communityStatSliderWrapper">
            <div className="communityStatHeaderRow">
              <h5 className="fw-medium mb-3">Statistics</h5>
              {/* Only CLs with permissionType "both" get a market toggle —
                  spot-only/futures-only CLs only ever have one market to show. */}
              {canToggleModalMarket && (
                <div
                  className="communityStatMarketToggle"
                  role="radiogroup"
                  aria-label="Statistics market"
                >
                  {["futures", "spot"].map((key) => (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={modalMarket === key}
                      className={`communityStatMarketToggle__btn${
                        modalMarket === key ? " is-active" : ""
                      }`}
                      onClick={() => setModalMarket(key)}
                    >
                      {key === "futures" ? "Futures" : "Spot"}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Swiper
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className="communityStatSwiper"
              slidesPerView="auto"
              spaceBetween={23}
            >
              {/* Hidden on mobile — shown below the swiper instead */}
              <SwiperSlide className="communityStatSwiper__rankingSlide desktopOnly">
                <div className="communityStatItem groupRankingStatItem">
                  <p className="fw-medium fs-18">Group Ranking</p>
                  <p className="fs-12">Among all trading groups</p>
                  <p className="fw-medium fs-22 d-flex justify-content-end">
                    {selectedCard?.myRanking || "-"}&nbsp;/&nbsp;
                    {selectedCard?.totalCommunities || "-"}
                  </p>
                </div>
              </SwiperSlide>
              {getStatsDataFromCard(selectedCard, modalMarket).map((stat, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="communityStatItem"
                    style={{
                      minHeight: "84.71px",
                      borderRadius: "10px",
                      backgroundColor: "#f7f7f7",
                      boxShadow: "0px 15px 24px rgba(0, 0, 0, 0.05)",
                      padding: "13.7px 13.5px",
                    }}
                  >
                    <p
                      className="title"
                      style={{
                        fontSize: "12px",
                        fontFamily: "Graphik Regular",
                        marginBottom: 0,
                        height: 32,
                      }}
                    >
                      {stat.title}
                    </p>
                    <p
                      className="numbers"
                      style={{
                        fontSize: "22px",
                        fontFamily: "Graphik Medium",
                        marginBottom: 0,
                      }}
                    >
                      {stat.numbers}
                    </p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Group Ranking shown below stats on mobile only */}
            <div className="communityStatItem groupRankingStatItem mobileOnly mt-3">
              <p className="fw-medium fs-18">Group Ranking</p>
              <p className="fs-12">Among all trading groups</p>
              <p className="fw-medium fs-22 d-flex justify-content-end">
                {selectedCard?.myRanking || "-"}&nbsp;/&nbsp;
                {selectedCard?.totalCommunities || "-"}
              </p>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <ConfirmModal
        show={showSwitchConfirm}
        className="marketplaceSwitchConfirmModal"
        onHide={() => !switchLoading && setShowSwitchConfirm(false)}
        title="Switch community?"
        description={`Cancel your subscription to "${subscribedCommunityName}" and join "${targetCommunityName}".`}
        confirmText="Cancel & Join"
        onConfirm={() => handleSwitchCommunity(selectedCard)}
        loading={switchLoading}
      />

      {showSetupModal && (
        <NoLinkMemberSetupScreen
          setShowSetupModal={setShowSetupModal}
          selectedCard={selectedCard}
        />
      )}
    </div>
  );
};

export default MarketPlace;

